import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw, Printer, ArrowLeft, UserMinus, Crosshair, Map as MapIcon } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DAYS, FormDialog, Loading, Pill, Table, Title, confirmRun, fmtDT, useData } from "@/ops/ui";
import { projectFields } from "@/ops/admin/Projects";
import { getPosition, mapsEmbed, mapsLink } from "@/ops/filters";

const CP_HEAD = ["Sıra", "Kod", "Nokta", "Konum", "Yarıçap", "Durum", ""];
const PATROL_HEAD = ["Güzergâh", "Noktalar", "Saatler", "Günler", "Tolerans", "Durum", ""];
const STAFF_HEAD = ["Sicil", "Personel", "Telefon", ""];

function MapDialog({ cp, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="cp-map-dialog">
        <DialogHeader><DialogTitle>{cp.name} – Kayıtlı Konum</DialogTitle></DialogHeader>
        <iframe title="Harita" src={mapsEmbed(cp.lat, cp.lng)} className="h-80 w-full rounded border-0" loading="lazy" referrerPolicy="no-referrer" />
        <p className="text-xs text-zinc-500">{cp.lat.toFixed(6)}, {cp.lng.toFixed(6)} · doğruluk ±{Math.round(cp.loc_accuracy || 0)} m · {cp.loc_set_at ? `${fmtDT(cp.loc_set_at)} · ${cp.loc_set_by}` : "elle girildi"}</p>
        <ul className="max-h-32 overflow-y-auto text-xs text-zinc-500">{(cp.loc_history || []).slice().reverse().map((h) => <li key={h.at}>{fmtDT(h.at)} – {h.by}: {h.lat.toFixed(6)}, {h.lng.toFixed(6)} (±{Math.round(h.accuracy || 0)} m)</li>)}</ul>
        <a href={mapsLink(cp.lat, cp.lng)} target="_blank" rel="noreferrer" className="btn-outline w-fit !py-2">Google Haritalar'da aç</a>
      </DialogContent>
    </Dialog>
  );
}

async function captureLocation(c, reload) {
  const msg = c.lat != null ? `${c.name} için konum yeniden kalibre edilsin mi? Kontrol noktasının yanında olmalısınız.` : `${c.name} için şu anki konumunuz kaydedilsin mi? Kontrol noktasının yanında olmalısınız.`;
  if (!window.confirm(msg)) return;
  try {
    const pos = await getPosition();
    await api.post(`/ops/admin/checkpoints/${c.id}/location`, { lat: pos.latitude, lng: pos.longitude, accuracy: pos.accuracy });
    toast.success(`Konum kaydedildi (±${Math.round(pos.accuracy)} m)`);
    reload();
  } catch (e) {
    toast.error(e.response ? errMsg(e) : e.message);
  }
}

const CP_FIELDS = [{ k: "name", label: "Nokta adı", required: true, wide: true }, { k: "order", label: "Sıra", type: "number" },
  { k: "radius_m", label: "Özel yarıçap (m, boşsa proje yarıçapı)", type: "number" },
  { k: "lat", label: "Enlem (isteğe bağlı, elle)", type: "number", step: "any", hint: "Tavsiye: listedeki 'Konumu kaydet' düğmesiyle sahada otomatik kaydedin." }, { k: "lng", label: "Boylam", type: "number", step: "any" },
  { k: "active", label: "QR kod aktif", type: "bool" }];

function Checkpoints({ pid }) {
  const [rows, reload] = useData(`/ops/admin/projects/${pid}/checkpoints`);
  const [form, setForm] = useState(null);
  const [map, setMap] = useState(null);
  const save = (v) => (form.id ? api.put(`/ops/admin/checkpoints/${form.id}`, v) : api.post(`/ops/admin/projects/${pid}/checkpoints`, v)).then(() => toast.success("Kontrol noktası kaydedildi"));
  const rotate = (c) => confirmRun("QR kod yenilensin mi? Eski basılı QR kod artık çalışmaz; yenisini basıp asmanız gerekir.", async () => { await api.post(`/ops/admin/checkpoints/${c.id}/rotate`); reload(); }, "QR kod yenilendi");
  const del = (c) => confirmRun(`${c.name} silinsin mi?`, async () => { await api.delete(`/ops/admin/checkpoints/${c.id}`); reload(); }, "Silindi");
  if (!rows) return <Loading />;
  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2">
        <button onClick={() => setForm({ active: true, order: rows.length + 1 })} className="btn-red !py-2" data-testid="checkpoint-add-btn"><Plus className="h-4 w-4" /> Nokta Ekle</button>
        <Link to={`/operasyon/projeler/${pid}/qr`} target="_blank" className="btn-outline !py-2" data-testid="checkpoint-print-btn"><Printer className="h-4 w-4" /> QR Kodları Yazdır</Link>
      </div>
      <Table head={CP_HEAD} testid="checkpoints-table">
        {rows.map((c) => (
          <tr key={c.id} data-testid={`checkpoint-row-${c.id}`}>
            <td className="px-3 py-2">{c.order}</td><td className="px-3 py-2 font-semibold text-[color:var(--red)]">{c.code}</td><td className="px-3 py-2 font-semibold">{c.name}</td>
            <td className="px-3 py-2 text-xs">{c.lat != null ? <><button onClick={() => setMap(c)} className="inline-flex items-center gap-1 font-semibold text-blue-700" data-testid={`checkpoint-map-${c.id}`}><MapIcon className="h-3 w-3" /> Haritada gör</button><p className="text-zinc-500">{c.loc_set_at ? `${fmtDT(c.loc_set_at)} · ${c.loc_set_by}` : "Elle girildi"}</p></> : <span className="font-semibold text-orange-700">Kaydedilmedi</span>}
              <button onClick={() => captureLocation(c, reload)} className="mt-1 flex items-center gap-1 font-semibold text-[color:var(--red)]" data-testid={`checkpoint-capture-${c.id}`}><Crosshair className="h-3 w-3" /> {c.lat != null ? "Yeniden kalibre et" : "Konumu kaydet"}</button></td><td className="px-3 py-2">{c.radius_m ? `${c.radius_m} m` : "Proje / varsayılan"}</td>
            <td className="px-3 py-2"><Pill ok={c.active}>{c.active ? "QR aktif" : "QR pasif"}</Pill></td>
            <td className="whitespace-nowrap px-3 py-2">
              <button onClick={() => setForm(c)} className="rounded p-1.5 hover:bg-zinc-100" title="Düzenle / QR aktif-pasif" data-testid={`checkpoint-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => rotate(c)} className="rounded p-1.5 hover:bg-zinc-100" title="QR kodu yenile" data-testid={`checkpoint-rotate-${c.id}`}><RefreshCw className="h-4 w-4" /></button>
              <button onClick={() => del(c)} className="rounded p-1.5 text-red-700 hover:bg-red-50" title="Sil" data-testid={`checkpoint-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
            </td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">Henüz kontrol noktası yok.</td></tr>}
      </Table>
      <FormDialog open={!!form} title={form?.id ? "Noktayı Düzenle" : "Yeni Kontrol Noktası"} fields={CP_FIELDS} initial={form} onSubmit={save} onClose={(ok) => { setForm(null); if (ok) reload(); }} />
      {map && <MapDialog cp={map} onClose={() => setMap(null)} />}
    </div>
  );
}

function PatrolDialog({ pid, item, cps, onClose }) {
  const [v, setV] = useState({ name: "", checkpoint_ids: [], times: "", days: [0, 1, 2, 3, 4, 5, 6], tolerance_min: 30, active: true, photo_required: false, ...item, ...(item?.times ? { times: item.times.join(", ") } : {}) });
  const tog = (k, x) => setV({ ...v, [k]: v[k].includes(x) ? v[k].filter((y) => y !== x) : [...v[k], x] });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...v, times: v.times.split(/[,\s]+/).filter(Boolean), tolerance_min: Number(v.tolerance_min) };
    try {
      await (item?.id ? api.put(`/ops/admin/patrols/${item.id}`, body) : api.post(`/ops/admin/projects/${pid}/patrols`, body));
      toast.success("Güzergâh kaydedildi");
      onClose(true);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="patrol-dialog">
        <DialogHeader><DialogTitle>{item?.id ? "Güzergâhı Düzenle" : "Yeni Devriye Güzergâhı"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-3 text-sm">
          <label className="grid gap-1 font-semibold">Güzergâh adı<input className="field-input" required value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="patrol-name" /></label>
          <div><p className="mb-1 font-semibold">Kontrol noktaları</p><div className="grid gap-1 rounded border border-zinc-200 p-2">{cps.map((c) => <label key={c.id} className="flex items-center gap-2"><input type="checkbox" checked={v.checkpoint_ids.includes(c.id)} onChange={() => tog("checkpoint_ids", c.id)} data-testid={`patrol-cp-${c.id}`} /> {c.order}. {c.name}</label>)}</div></div>
          <label className="grid gap-1 font-semibold">Planlanan saatler<input className="field-input" required placeholder="08:00, 12:00, 16:00" value={v.times} onChange={(e) => setV({ ...v, times: e.target.value })} data-testid="patrol-times" /><span className="font-normal text-zinc-500">Virgülle ayırın, 24 saat biçimi (Türkiye saati).</span></label>
          <div><p className="mb-1 font-semibold">Günler</p><div className="flex flex-wrap gap-2">{DAYS.map((d, i) => <label key={d} className="flex items-center gap-1 rounded border border-zinc-200 px-2 py-1"><input type="checkbox" checked={v.days.includes(i)} onChange={() => tog("days", i)} data-testid={`patrol-day-${i}`} /> {d}</label>)}</div></div>
          <label className="grid gap-1 font-semibold">Gecikme toleransı (dakika)<input type="number" min={5} max={240} className="field-input" value={v.tolerance_min} onChange={(e) => setV({ ...v, tolerance_min: e.target.value })} data-testid="patrol-tolerance" /></label>
          <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={!!v.photo_required} onChange={(e) => setV({ ...v, photo_required: e.target.checked })} data-testid="patrol-photo" /> Fotoğraf zorunlu (QR + GPS + fotoğraf olmadan tamamlanmış sayılmaz)</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={v.active} onChange={(e) => setV({ ...v, active: e.target.checked })} /> Aktif</label>
          <button className="btn-red justify-center !py-2" data-testid="patrol-submit">Kaydet</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Patrols({ pid }) {
  const [rows, reload] = useData(`/ops/admin/projects/${pid}/patrols`);
  const [cps] = useData(`/ops/admin/projects/${pid}/checkpoints`);
  const [form, setForm] = useState(null);
  if (!rows || !cps) return <Loading />;
  const name = Object.fromEntries(cps.map((c) => [c.id, c.name]));
  const del = (p) => confirmRun(`${p.name} silinsin mi?`, async () => { await api.delete(`/ops/admin/patrols/${p.id}`); reload(); }, "Silindi");
  return (
    <div>
      <button onClick={() => (cps.length ? setForm({}) : toast.error("Önce kontrol noktası ekleyin."))} className="btn-red mb-3 !py-2" data-testid="patrol-add-btn"><Plus className="h-4 w-4" /> Güzergâh Ekle</button>
      <Table head={PATROL_HEAD} testid="patrols-table">
        {rows.map((p) => (
          <tr key={p.id} data-testid={`patrol-row-${p.id}`}>
            <td className="px-3 py-2 font-semibold">{p.name}</td><td className="px-3 py-2 text-xs">{p.checkpoint_ids.map((c) => name[c] || "(silinmiş)").join(" → ")}</td>
            <td className="px-3 py-2">{p.times.join(", ")}</td><td className="px-3 py-2 text-xs">{p.days.map((d) => DAYS[d]).join(", ")}</td><td className="px-3 py-2">{p.tolerance_min} dk</td>
            <td className="px-3 py-2"><Pill ok={p.active}>{p.active ? "Aktif" : "Pasif"}</Pill></td>
            <td className="whitespace-nowrap px-3 py-2"><button onClick={() => setForm(p)} className="rounded p-1.5 hover:bg-zinc-100" data-testid={`patrol-edit-${p.id}`}><Pencil className="h-4 w-4" /></button><button onClick={() => del(p)} className="rounded p-1.5 text-red-700 hover:bg-red-50" data-testid={`patrol-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button></td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">Henüz güzergâh yok.</td></tr>}
      </Table>
      {form && <PatrolDialog pid={pid} item={form} cps={cps} onClose={(ok) => { setForm(null); if (ok) reload(); }} />}
    </div>
  );
}

function Staff({ pid }) {
  const [rows, reload] = useData(`/ops/admin/projects/${pid}/assignments`);
  const [emps] = useData("/ops/admin/employees");
  const [sel, setSel] = useState("");
  const free = useMemo(() => {
    const assigned = new Set((rows || []).map((r) => r.employee_id));
    return (emps || []).filter((e) => e.active && !assigned.has(e.id));
  }, [rows, emps]);
  const add = async () => {
    try {
      await api.post(`/ops/admin/projects/${pid}/assignments`, { employee_id: sel });
      setSel("");
      reload();
      toast.success("Personel atandı");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!rows || !emps) return <Loading />;
  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2">
        <select value={sel} onChange={(e) => setSel(e.target.value)} className="field-input max-w-sm" data-testid="assign-select"><option value="">Personel seçin...</option>{free.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.sicil_no})</option>)}</select>
        <button disabled={!sel} onClick={add} className="btn-red !py-2" data-testid="assign-add-btn"><Plus className="h-4 w-4" /> Ata</button>
      </div>
      <Table head={STAFF_HEAD} testid="assignments-table">
        {rows.map((r) => (
          <tr key={r.employee_id}><td className="px-3 py-2">{r.employee.sicil_no}</td><td className="px-3 py-2 font-semibold">{r.employee.name}</td><td className="px-3 py-2">{r.employee.phone}</td>
            <td className="px-3 py-2"><button onClick={() => confirmRun("Personel projeden çıkarılsın mı?", async () => { await api.delete(`/ops/admin/projects/${pid}/assignments/${r.employee_id}`); reload(); }, "Çıkarıldı")} className="rounded p-1.5 text-red-700 hover:bg-red-50" data-testid={`assign-remove-${r.employee_id}`}><UserMinus className="h-4 w-4" /></button></td></tr>
        ))}
        {!rows.length && <tr><td colSpan={4} className="p-6 text-center text-zinc-500">Bu projeye personel atanmadı.</td></tr>}
      </Table>
    </div>
  );
}

const TABS = [["cps", "Kontrol Noktaları"], ["patrols", "Devriye Güzergâhları"], ["staff", "Atanan Personel"]];

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, reload] = useData(`/ops/admin/projects/${id}`);
  const [clients] = useData("/ops/admin/clients");
  const [tab, setTab] = useState("cps");
  const [edit, setEdit] = useState(false);
  if (!p) return <Loading />;
  const del = () => confirmRun("Proje silinsin mi? Atamalar kapatılır, geçmiş kayıtlar korunur.", async () => { await api.delete(`/ops/admin/projects/${id}`); window.location.href = "/operasyon/projeler"; }, "Proje silindi");
  return (
    <div>
      <Link to="/operasyon/projeler" className="mb-3 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" /> Projeler</Link>
      <Title title={`${p.code} · ${p.name}`} desc={`Kabul mesafesi ${p.radius_m ? `${p.radius_m} m` : "varsayılan"} · Fotoğraf ${p.photo_required ? "zorunlu" : "isteğe bağlı"} · Müşteriyle paylaşım ${p.share_patrols ? "açık" : "kapalı"} · Konum aralığı ${p.location_interval_min ? `${p.location_interval_min} dk` : "kapalı"}`}>
        <button onClick={() => setEdit(true)} className="btn-outline !py-2" data-testid="project-edit-btn"><Pencil className="h-4 w-4" /> Proje Ayarları</button>
        <button onClick={del} className="btn-outline !py-2 text-red-700" data-testid="project-delete-btn"><Trash2 className="h-4 w-4" /> Sil</button>
      </Title>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-zinc-200">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-semibold ${tab === k ? "border-[color:var(--red)] text-zinc-950" : "border-transparent text-zinc-500"}`} data-testid={`project-tab-${k}`}>{l}</button>)}
      </div>
      {tab === "cps" && <Checkpoints pid={id} />}
      {tab === "patrols" && <Patrols pid={id} />}
      {tab === "staff" && <Staff pid={id} />}
      <FormDialog open={edit} title="Proje Ayarları" fields={projectFields(clients)} initial={p} onSubmit={(v) => api.put(`/ops/admin/projects/${id}`, v).then(() => toast.success("Proje güncellendi"))} onClose={(ok) => { setEdit(false); if (ok) reload(); }} />
    </div>
  );
}
